import { type FC } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  MetricCard,
  type MetricCardData,
} from "@/partner/shared/components/MetricCard";
import {
  formatClock,
  formatGb,
  formatUsdCost,
  formatUsdPerMonth,
  useStorageBreakdownQuery,
} from "@/partner/entities/storage";
import { useIsBunnyProvider } from "@/partner/entities/platforms";

/**
 * Итоги по платформе. Запрос тот же, что у списка, но без страницы и
 * фильтра: итоги считаются по всей платформе, а Apollo дедуплицирует
 * одинаковые запросы, поэтому лишнего обращения к бэкенду нет.
 */
export const StorageTotals: FC = () => {
  const { t } = useTranslation();
  // У Bunny цена считается по объёму, поэтому во второй строке итогов
  // показываем занятые гигабайты, а не суммарную длительность.
  const isBunny = useIsBunnyProvider();
  const { data } = useStorageBreakdownQuery({
    variables: { page: 0, rowsPerPage: 1 },
    fetchPolicy: "cache-and-network",
  });
  const totals = data?.storageBreakdown?.totals;
  const emptyValue = t("common.emptyDash");

  const formatCost = isBunny ? formatUsdCost : formatUsdPerMonth;
  // У Bunny цена целиком зависит от объёма: если объём библиотеки узнать не
  // удалось (нет аккаунтного ключа bunny.net либо сбой core API), бэкенд
  // отдаёт нулевые суммы, и показывать их как $0.00 нельзя.
  const sizeUnavailable =
    isBunny && totals != null && totals.storageBytes == null;
  const unavailableValue = t("storage.totals.sizeUnavailable");
  const totalsVolume = sizeUnavailable
    ? unavailableValue
    : isBunny && totals?.storageBytes != null
      ? t("storage.list.sizeValue", { size: formatGb(totals.storageBytes) })
      : totals
        ? formatClock(totals.durationSeconds)
        : emptyValue;

  const formatTotalCost = (value: number) =>
    sizeUnavailable
      ? unavailableValue
      : t("storage.totals.perMonthValue", { amount: formatCost(value) });

  const totalCard: MetricCardData = {
    primary: {
      label: t("storage.totals.storageCostLabel"),
      value: totals ? formatTotalCost(totals.storageCostUsdMonth) : emptyValue,
      xs: 6,
    },
    secondary: {
      label: t("storage.totals.contentsCountLabel"),
      value: totals
        ? `${t("storage.totals.contentsCountValue", {
            count: totals.contentsCount,
          })} · ${totalsVolume}`
        : emptyValue,
      xs: 6,
    },
  };

  const unwatchedCard: MetricCardData = {
    primary: {
      label: t("storage.totals.unwatchedCostLabel"),
      value: totals
        ? formatTotalCost(totals.unwatchedCostUsdMonth)
        : emptyValue,
      xs: 6,
    },
    secondary: {
      label: t("storage.totals.unwatchedCountLabel"),
      value: totals
        ? t("storage.totals.unwatchedCountValue", {
            count: totals.unwatchedCount,
          })
        : emptyValue,
      xs: 6,
    },
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <MetricCard metric={totalCard} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <MetricCard metric={unwatchedCard} />
      </Grid>
    </Grid>
  );
};
